import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaSearch, FaBars, FaChevronDown } from 'react-icons/fa';
import SearchOverlay from '../ui/SearchOverlay';
import MobileMenu from './MobileMenu';
import graduateStudents from '../../cms/graduate-students.json';
import siteSettings from '../../cms/site-settings.json';
import { buildNavigation } from '../../utils/buildNavigation';
import '../../css/layout/Header.css';

const GRADUATE_STUDENTS_ENABLED = graduateStudents.enabled;

const FALLBACK_NAV = buildNavigation(
  {
    navigation: [
      { label: 'Home', path: '/', external: false, dropdown: [] },
      { label: 'About', path: '/about', external: false, dropdown: [{ label: 'FAQ', path: '/faq', external: false }] },
      { label: 'Alumni', path: '/alumni', external: false, dropdown: [] },
    ],
  },
  { graduateStudentsEnabled: GRADUATE_STUDENTS_ENABLED }
);

const CMS_NAV = buildNavigation(siteSettings, { graduateStudentsEnabled: GRADUATE_STUDENTS_ENABLED });
const NAV_ITEMS = CMS_NAV || FALLBACK_NAV;

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const [showDonateWarning, setShowDonateWarning] = useState(false);
  const [donateUrl, setDonateUrl] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const handleDonateClick = (e, url) => {
    e.preventDefault();
    setDonateUrl(url);
    setShowDonateWarning(true);
  };

  const confirmDonate = () => {
    window.open(donateUrl, '_blank', 'noopener,noreferrer');
    setShowDonateWarning(false);
  };

  const cancelDonate = () => {
    setShowDonateWarning(false);
  };

  const navItems = NAV_ITEMS.map((item) => {
    if (item.label === 'Donate' && siteSettings.donateUrl) {
      return { ...item, path: siteSettings.donateUrl, external: true };
    }
    return item;
  });

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen(null);
  };

  const handleDesktopDropdownEnter = (itemPath) => {
    setDesktopDropdownOpen(itemPath);
  };

  const handleDesktopDropdownLeave = () => {
    setDesktopDropdownOpen(null);
  };

  const toggleMobileDropdown = (itemPath) => {
    setMobileDropdownOpen(mobileDropdownOpen === itemPath ? null : itemPath);
  };

  return (
    <header className={`header-container ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-wrapper">
        <div className="nav-content">
          {/* Logo */}
          <Link to="/" className="header-logo">
            <div className="logo-text">
              <div className="logo-main">{siteSettings.logoMain}</div>
              <div className="logo-subtitle">{siteSettings.logoSubtitle}</div>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="nav">
            <ul className="nav-list">
              {navItems.map((item) => (
                <li key={item.path} className="nav-item">
                  {item.dropdown ? (
                    <div 
                      className="nav-dropdown"
                      onMouseEnter={() => handleDesktopDropdownEnter(item.path)}
                      onMouseLeave={handleDesktopDropdownLeave}
                    >
                      <Link to={item.path} className="nav-link">
                        {item.label} <FaChevronDown className={`dropdown-arrow ${desktopDropdownOpen === item.path ? 'open' : ''}`} />
                      </Link>
                      {desktopDropdownOpen === item.path && (
                        <ul className="dropdown-menu show">
                          {item.dropdown.map((dropdownItem) => (
                            <li key={dropdownItem.path}>
                              {dropdownItem.external ? (
                                <a 
                                  href={dropdownItem.path} 
                                  className="dropdown-link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {dropdownItem.label}
                                </a>
                              ) : (
                              <Link to={dropdownItem.path} className="dropdown-link">
                                {dropdownItem.label}
                              </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ) : item.external ? (
                    <a 
                      href={item.path} 
                      onClick={(e) => handleDonateClick(e, item.path)}
                      className="nav-link"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.path} className="nav-link">{item.label}</Link>
                  )}
                </li>
              ))}
              <li className="nav-item">
                <button
                  className="search-button"
                  aria-label="Search"
                  onClick={() => setShowSearch(true)}
                >
                  <FaSearch />
                </button>
              </li>
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Open mobile menu"
          >
            <FaBars />
          </button>
        </div>
      </div>

      <MobileMenu 
        navItems={navItems}
        isOpen={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        mobileDropdownOpen={mobileDropdownOpen}
        onToggleDropdown={toggleMobileDropdown}
        onSearchClick={() => setShowSearch(true)}
      />

      {showSearch && <SearchOverlay onClose={() => setShowSearch(false)} />}

      {/* External Link Warning Modal */}
      {showDonateWarning && (
        <div className="external-link-warning-overlay" onClick={cancelDonate}>
          <div className="external-link-warning-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{siteSettings.donateWarningTitle}</h3>
            <p>{siteSettings.donateWarningText}</p>
            <div className="external-link-warning-buttons">
              <button onClick={cancelDonate} className="cancel-button">Cancel</button>
              <button onClick={confirmDonate} className="confirm-button">Continue</button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;